/**
 * Right-side detail drawer for a picked Agent.
 *
 * Tabs (mirrors `/v1/agents` + `/v1/channels` + `/v1/activity` surfaces):
 *   - Setup        : profile + **LLM spec** (pick team model from /llm + runtime).
 *   - Capabilities : KB, skills, memory, tools, MCP, guardrails — dedicated tab;
 *                    save sends a **full** merged `AgentSpec` (gateway contract).
 *   - Versions  : lazy GET /v1/agents/{id}/versions; current version is
 *                 pinned to the top with an "active" pill.
 *   - Channels  : filter the team-wide bindings (already fetched once by
 *                 the parent) by `agent_id` and show kind/slug/enabled.
 *   - Runs      : lazy GET /v1/activity/sessions?agent_id=...; click a
 *                 row to jump back into /chat with the session preselected.
 *
 * The drawer is "view-only" by default. Toggling Edit on Setup or
 * Capabilities remounts the matching form from the latest agent row.
 * Cancel discards local edits; Save PATCHes once and exits edit mode after
 * the parent's `onChanged()` resolves.
 */

import {
  ArrowUpRight,
  BookOpen,
  Check,
  Clipboard,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Shield,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { LetterAvatar } from "@/components/chat/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { modelFromLlmConfigSummary, readLlmConfigListItems } from "@/lib/agent-llm-preset";
import { api } from "@/lib/api";
import { setChatPreselectedAgentId } from "@/lib/chat-preselect";
import {
  AGENT_RUNTIMES,
  type AgentGuardrailRef,
  type AgentMcpServerRef,
  type AgentMemorySpec,
  type AgentRuntimeName,
  type AgentSkillRef,
  type AgentSpec,
  type AgentSummary,
  type AgentToolRef,
  type AgentVersion,
  type ChannelBindingSummary,
  DEFAULT_AGENT_SPEC,
  type ExternalMcpListResponse,
  type ExternalMcpSummary,
  type InstalledSkillsResponse,
  KNOWN_BUILTIN_TOOLS,
  type KbView,
  type LLMConfigListResponse,
  type LLMConfigSummary,
  type SkillSpec,
  TOOL_POLICIES,
  TOOL_RISK_TIERS,
  type ToolPolicy,
  type ToolRiskTier,
  type UpdateAgentRequest,
} from "@/lib/types";
import { cn } from "@/lib/utils";

interface AgentDrawerProps {
  agent: AgentSummary;
  kbs: KbView[];
  kbsById: Map<string, KbView>;
  bindings: ChannelBindingSummary[];
  isAdmin: boolean;
  onClose: () => void;
  onChat: () => void;
  onArchive: () => void;
  /** Reverse a previous archive. Visible only when `agent.is_archived`. */
  onRestore: () => Promise<void> | void;
  /** Refresh upstream `agents` list after a successful save. */
  onChanged: () => Promise<void> | void;
}

type DrawerTab = "setup" | "capabilities" | "prompt" | "versions" | "channels" | "runs";

type EditingSlice = null | "setup" | "capabilities";

export function AgentDrawer({
  agent,
  kbs,
  kbsById,
  bindings,
  isAdmin,
  onClose,
  onChat,
  onArchive,
  onRestore,
  onChanged,
}: AgentDrawerProps) {
  const spec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const archived = agent.is_archived;
  const [tab, setTab] = useState<DrawerTab>("setup");
  const [editingSlice, setEditingSlice] = useState<EditingSlice>(null);
  const [copied, setCopied] = useState(false);
  // Reset on agent switch — otherwise edit state leaks between rows
  // and the header buttons mismatch the body content.
  // biome-ignore lint/correctness/useExhaustiveDependencies: agent.id is the trigger; setters are stable
  useEffect(() => {
    setTab("setup");
    setEditingSlice(null);
    setCopied(false);
  }, [agent.id]);

  const agentBindings = useMemo(
    () => bindings.filter((b) => b.agent_id === agent.id),
    [bindings, agent.id],
  );

  async function copySpec(): Promise<void> {
    try {
      // Pretty-print so the clipboard payload is something you can
      // paste straight into a runbook / Notion. Includes the spec
      // *only* — agent.id / created_at would just be noise.
      await navigator.clipboard.writeText(JSON.stringify(spec, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Copy failed");
    }
  }

  return (
    <>
      <SheetHeader className="flex shrink-0 flex-row items-center gap-2 border-b border-border/70 px-4 py-2.5">
        <LetterAvatar seed={agent.id} label={agent.name} size="sm" />
        <div className="flex min-w-0 flex-1 flex-col">
          <SheetTitle className="truncate">{agent.name}</SheetTitle>
          <SheetDescription className="truncate">
            {spec.runtime} · <span className="font-mono">{spec.model.model}</span> ·{" "}
            <span className="font-mono">{agent.id.slice(0, 8)}</span>
          </SheetDescription>
        </div>
        <span
          className={cn(
            "inline-flex h-5 items-center gap-1 rounded-sm px-1.5 text-[10px] font-medium uppercase tracking-wide",
            archived
              ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
              : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
          )}
        >
          <span
            className={cn("h-1.5 w-1.5 rounded-full", archived ? "bg-amber-500" : "bg-emerald-500")}
          />
          {archived ? "Archived" : "Active"}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={onChat}
              disabled={archived}
              aria-label="Open in chat"
            >
              <MessageSquare className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Open in chat</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => void copySpec()}
              aria-label="Copy spec JSON"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Clipboard className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{copied ? "Copied" : "Copy spec JSON"}</TooltipContent>
        </Tooltip>
        {/* Edit pencil for Setup / Capabilities; Prompt has its own control. */}
        {isAdmin &&
        editingSlice === null &&
        (tab === "setup" || tab === "capabilities") &&
        !archived ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setEditingSlice(tab === "setup" ? "setup" : "capabilities")}
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {tab === "setup" ? "Edit setup" : "Edit capabilities"}
            </TooltipContent>
          </Tooltip>
        ) : null}
        {isAdmin && archived ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-emerald-600"
                onClick={() => void onRestore()}
                aria-label="Restore"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Restore from archive</TooltipContent>
          </Tooltip>
        ) : isAdmin ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={onArchive}
                aria-label="Archive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Archive</TooltipContent>
          </Tooltip>
        ) : null}
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </SheetHeader>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          // Switching tabs cancels an in-progress edit. The save form
          // is destructive enough that we surface it; we drop the
          // changes silently here because tab-clicks aren't usually
          // intentional commits.
          if (editingSlice !== null) setEditingSlice(null);
          setTab(v as DrawerTab);
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="shrink-0 border-b border-border/70 px-3 py-2">
          <TabsList className="h-8 rounded-md bg-muted/60 p-0.5 text-[11px]">
            <TabsTrigger value="setup" className="h-7 rounded-md px-3">
              Setup
            </TabsTrigger>
            <TabsTrigger value="capabilities" className="h-7 rounded-md px-3">
              Capabilities
            </TabsTrigger>
            <TabsTrigger value="prompt" className="h-7 rounded-md px-3">
              Prompt
            </TabsTrigger>
            <TabsTrigger value="versions" className="h-7 rounded-md px-3">
              Versions
            </TabsTrigger>
            <TabsTrigger value="channels" className="h-7 rounded-md px-3">
              Channels
              {agentBindings.length > 0 ? (
                <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-foreground/10 px-1 text-[9px] font-semibold text-foreground">
                  {agentBindings.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="runs" className="h-7 rounded-md px-3">
              Runs
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="setup"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          {editingSlice === "setup" ? (
            <SetupEditForm
              key={agent.id}
              agent={agent}
              onCancel={() => setEditingSlice(null)}
              onSaved={async () => {
                await onChanged();
                setEditingSlice(null);
              }}
            />
          ) : (
            <SetupView agent={agent} />
          )}
        </TabsContent>

        <TabsContent
          value="capabilities"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          {editingSlice === "capabilities" ? (
            <CapabilitiesEditForm
              agent={agent}
              kbs={kbs}
              onCancel={() => setEditingSlice(null)}
              onSaved={async () => {
                await onChanged();
                setEditingSlice(null);
              }}
            />
          ) : (
            <CapabilitiesView agent={agent} kbsById={kbsById} />
          )}
        </TabsContent>

        <TabsContent
          value="prompt"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          <PromptPanel agent={agent} canEdit={isAdmin && !archived} onSaved={onChanged} />
        </TabsContent>

        <TabsContent
          value="versions"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          <VersionsPanel agent={agent} />
        </TabsContent>

        <TabsContent
          value="channels"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          <ChannelsPanel bindings={agentBindings} canManage={isAdmin} onChanged={onChanged} />
        </TabsContent>

        <TabsContent
          value="runs"
          className="m-0 min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[12px]"
        >
          <RunsPanel agent={agent} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Setup + Capabilities — view mode
// ────────────────────────────────────────────────────────────────────

function SetupView({ agent }: { agent: AgentSummary }) {
  const spec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const presetId = spec.llm_config_id;

  const [llmConfigs, setLlmConfigs] = useState<LLMConfigSummary[] | null>(null);
  useEffect(() => {
    if (!presetId) {
      setLlmConfigs(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<LLMConfigListResponse>("/llm/configs");
        if (!cancelled) {
          const { items, badShape } = readLlmConfigListItems(res);
          setLlmConfigs(badShape ? [] : items);
        }
      } catch {
        if (!cancelled) setLlmConfigs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presetId]);

  const presetRow = useMemo(() => {
    if (!presetId) return null;
    if (llmConfigs === null) {
      return ["Model", "Loading…", null] as [string, string, null];
    }
    const cfg = llmConfigs.find((c) => c.id === presetId);
    if (!cfg) {
      return ["Model", `${presetId.slice(0, 8)}… (not in team list)`, null] as [
        string,
        string,
        null,
      ];
    }
    const title = cfg.display_name ? `${cfg.slug} · ${cfg.display_name}` : cfg.slug;
    return ["Model", title, null] as [string, string, null];
  }, [presetId, llmConfigs]);

  const llmSpecRows: Array<[string, string, null]> = useMemo(() => {
    if (!presetId) {
      return [
        ["Runtime", spec.runtime, null],
        [
          "Model",
          "Not bound — edit Setup and pick a team model from /llm (provider + model id live on that row).",
          null,
        ],
      ];
    }
    const cfg = llmConfigs?.find((c) => c.id === presetId);
    const rows: Array<[string, string, null]> = [
      ["Runtime", spec.runtime, null],
      ...(presetRow ? [presetRow] : []),
    ];
    if (cfg) {
      rows.push(
        ["Temperature", String(cfg.temperature), null],
        [
          "Max output tokens",
          cfg.max_output_tokens === null ? "—" : String(cfg.max_output_tokens),
          null,
        ],
        ["API model id", cfg.model, null],
      );
    } else if (llmConfigs !== null) {
      rows.push(
        ["API model id", spec.model.model, null],
        ["Temperature", String(spec.model.temperature), null],
        [
          "Max output tokens",
          spec.model.max_output_tokens === null ? "—" : String(spec.model.max_output_tokens),
          null,
        ],
      );
    }
    return rows;
  }, [presetId, presetRow, llmConfigs, spec]);

  return (
    <div className="space-y-6">
      {agent.description ? (
        <p className="leading-relaxed text-muted-foreground">{agent.description}</p>
      ) : null}

      <section className="space-y-3 rounded-lg border border-border/80 bg-card/40 p-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">
          LLM spec
        </h3>
        <p className="text-[10px] leading-snug text-muted-foreground">
          {presetId ? (
            <>
              Bound to one <span className="font-medium text-foreground">team model</span> from{" "}
              <code className="text-[10px]">/llm</code> — provider, API model id, and endpoint all
              come from that single row.
            </>
          ) : (
            <>
              This agent is not bound to a team model yet. Under{" "}
              <code className="text-[10px]">/llm</code> each row{" "}
              <span className="font-medium text-foreground">is one model</span> (provider + API
              model id + parameters); bind one in Setup → Edit.
            </>
          )}
        </p>
        <Section label="Model & runtime">
          <DataGrid rows={llmSpecRows} />
        </Section>
      </section>

      <Section label="Lifecycle">
        <DataGrid
          rows={[
            ["ID", agent.id, null],
            ["Created", new Date(agent.created_at).toLocaleString(), null],
            ["Updated", new Date(agent.updated_at).toLocaleString(), null],
            ["Version", agent.current_version_id ?? "—", null],
          ]}
        />
      </Section>
    </div>
  );
}

function CapabilitiesView({
  agent,
  kbsById,
}: {
  agent: AgentSummary;
  kbsById: Map<string, KbView>;
}) {
  const spec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const linkedKbs = spec.knowledge_base_ids.map((id) => ({
    id,
    name: kbsById.get(id)?.name ?? `KB:${id.slice(0, 8)}`,
    scope: kbsById.get(id)?.scope ?? null,
  }));

  return (
    <div className="space-y-4">
      <p className="text-[10px] leading-snug text-muted-foreground">
        Knowledge, tools, memory, and policies — same versioned{" "}
        <code className="text-[10px]">spec</code> as Setup; use the tabs to focus LLM vs
        capabilities.
      </p>

      <Section label={`Knowledge bases (${linkedKbs.length})`}>
        {linkedKbs.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No KBs attached.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {linkedKbs.map((kb) => (
              <Badge
                key={kb.id}
                variant="outline"
                className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
              >
                <BookOpen className="mr-0.5 h-2.5 w-2.5" />
                {kb.name}
                {kb.scope ? <span className="ml-1 text-muted-foreground">{kb.scope}</span> : null}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section label={`Skills (${spec.skills.length})`}>
        {spec.skills.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No skills bound. Toggle Edit on Capabilities to attach skills installed for the team.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {spec.skills.map((s) => (
              <Badge
                key={s.id}
                variant="outline"
                className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
                title={s.version ? `pinned to v${s.version}` : "live catalog version"}
              >
                <Sparkles className="mr-0.5 h-2.5 w-2.5" />
                {s.id}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section label={`Tools (${spec.tools.length})`}>
        {spec.tools.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No tools wired. Toggle Edit on Capabilities to attach builtin or MCP tools.
          </p>
        ) : (
          <ul className="space-y-1">
            {spec.tools.map((t, i) => {
              const tier: ToolRiskTier = t.risk_tier ?? "safe";
              const policy: ToolPolicy = t.policy ?? "allow";
              return (
                <li
                  key={`${t.kind}:${t.name}:${i}`}
                  className="flex flex-wrap items-center gap-1 rounded-md border border-border/60 bg-card/60 px-2 py-1.5"
                >
                  <Badge
                    variant="outline"
                    className="h-5 rounded-sm border-border/70 px-1.5 font-mono text-[10px] font-normal"
                  >
                    {t.kind}:{t.name}
                  </Badge>
                  <RiskTierBadge tier={tier} />
                  <PolicyBadge policy={policy} />
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section label={`MCP servers (${spec.mcp_servers.length})`}>
        {spec.mcp_servers.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No MCP servers attached.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {spec.mcp_servers.map((m) => (
              <Badge
                key={m.name}
                variant="outline"
                className="h-5 rounded-sm border-border/70 px-1.5 font-mono text-[10px] font-normal"
              >
                {m.name}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section label="Memory">
        <DataGrid
          rows={[
            ["Short-term token budget", String(spec.memory.short_term_token_budget), null],
            [
              "Summarize every N turns",
              spec.memory.summarize_every_turns === null
                ? "—"
                : String(spec.memory.summarize_every_turns),
              spec.memory.summarize_every_turns === null ? "off" : null,
            ],
            ["Episodic recall", spec.memory.episodic_recall ? "on" : "off", null],
          ]}
        />
      </Section>

      <Section label={`Guardrails (${spec.guardrails.length})`}>
        {spec.guardrails.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No guardrails configured.</p>
        ) : (
          <ul className="space-y-1">
            {spec.guardrails.map((g, i) => (
              <li
                key={`${g.name}-${i}`}
                className="flex items-start gap-2 rounded-md border border-border/60 bg-card/60 px-2 py-1.5"
              >
                <Shield className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-foreground">{g.name}</div>
                  {Object.keys(g.config).length > 0 ? (
                    <pre className="mt-0.5 overflow-x-auto rounded bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {JSON.stringify(g.config)}
                    </pre>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  );
}

// `risk_tier` is an *informational* axis (safe / risky / dangerous) used by
// downstream eval + UX. It DOES NOT gate execution on its own — that's
// `policy`'s job. Colour mirrors the same severity scale used in the
// archive / lifecycle pills above to keep the drawer visually coherent.
function RiskTierBadge({ tier }: { tier: ToolRiskTier }) {
  const cls =
    tier === "dangerous"
      ? "bg-destructive/10 text-destructive"
      : tier === "risky"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex h-4 items-center rounded-sm px-1.5 text-[9px] font-medium uppercase tracking-wide",
        cls,
      )}
      title={`risk_tier=${tier}`}
    >
      {tier}
    </span>
  );
}

// `policy` is the *enforcement* axis. ``allow`` runs the tool inline,
// ``require_approval`` parks the run in waiting_approval (L2 work),
// ``deny`` short-circuits with a guardrail-style refusal. We surface
// require_approval as amber so it visually overlaps with how Approvals
// rows will render in the (forthcoming) /approvals page.
function PolicyBadge({ policy }: { policy: ToolPolicy }) {
  if (policy === "allow") return null; // default; no chrome needed
  const cls =
    policy === "deny"
      ? "bg-destructive/10 text-destructive"
      : "bg-amber-500/10 text-amber-700 dark:text-amber-400";
  const label = policy === "require_approval" ? "approval" : "deny";
  return (
    <span
      className={cn(
        "inline-flex h-4 items-center rounded-sm px-1.5 text-[9px] font-medium uppercase tracking-wide",
        cls,
      )}
      title={`policy=${policy}`}
    >
      {label}
    </span>
  );
}

function DataGrid({ rows }: { rows: Array<[string, string, string | null]> }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px]">
      {rows.map(([k, v, hint]) => (
        <Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="break-all font-mono text-foreground">
            {v}
            {hint ? <span className="ml-1 text-muted-foreground">({hint})</span> : null}
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

// ────────────────────────────────────────────────────────────────────
// Setup — inline edit (profile + LLM only)
// ────────────────────────────────────────────────────────────────────

function SetupEditForm({
  agent,
  onCancel,
  onSaved,
}: {
  agent: AgentSummary;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const initialSpec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description ?? "");
  const [runtime, setRuntime] = useState<AgentRuntimeName>(initialSpec.runtime);
  const [llmConfigId, setLlmConfigId] = useState<string | null>(initialSpec.llm_config_id);
  const [submitting, setSubmitting] = useState(false);

  const [llmConfigs, setLlmConfigs] = useState<LLMConfigSummary[] | null>(null);
  const [llmConfigsError, setLlmConfigsError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<LLMConfigListResponse>("/llm/configs");
        if (!cancelled) {
          const { items, badShape } = readLlmConfigListItems(res);
          setLlmConfigs(items);
          setLlmConfigsError(
            badShape ? "Invalid response from /llm/configs — refresh or check the gateway." : null,
          );
        }
      } catch (err) {
        if (!cancelled) {
          setLlmConfigs([]);
          setLlmConfigsError(err instanceof Error ? err.message : "Failed to load team models");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedConfig = useMemo<LLMConfigSummary | null>(() => {
    if (!llmConfigId || llmConfigs === null) return null;
    return llmConfigs.find((c) => c.id === llmConfigId) ?? null;
  }, [llmConfigId, llmConfigs]);

  const presetsReady = llmConfigs !== null;
  const hasTeamModels = presetsReady && llmConfigs.length > 0;
  const isOrphanPresetBinding = llmConfigId !== null && presetsReady && selectedConfig === null;
  const modelPickUnset = "__model_unset__";
  const canSaveLlm = hasTeamModels && !!llmConfigId && !!selectedConfig && !isOrphanPresetBinding;

  function mergeSetupIntoSpec(): AgentSpec {
    if (llmConfigId && selectedConfig) {
      return {
        ...initialSpec,
        runtime,
        llm_config_id: llmConfigId,
        model: modelFromLlmConfigSummary(selectedConfig, initialSpec.model),
        system_prompt: initialSpec.system_prompt,
      };
    }
    if (llmConfigId && presetsReady && !selectedConfig) {
      return {
        ...initialSpec,
        runtime,
        llm_config_id: llmConfigId,
        model: { ...initialSpec.model },
        system_prompt: initialSpec.system_prompt,
      };
    }
    return {
      ...initialSpec,
      runtime,
      llm_config_id: null,
      model: { ...initialSpec.model },
      system_prompt: initialSpec.system_prompt,
    };
  }

  async function submit(): Promise<void> {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!presetsReady) {
      toast.error("Team models are still loading");
      return;
    }
    if (!hasTeamModels) {
      toast.error("Add at least one model under /llm before binding an agent");
      return;
    }
    if (isOrphanPresetBinding) {
      toast.error("That model no longer exists in /llm — pick another");
      return;
    }
    if (!llmConfigId || !selectedConfig) {
      toast.error("Select a model");
      return;
    }
    const spec = mergeSetupIntoSpec();
    setSubmitting(true);
    try {
      const body: UpdateAgentRequest = {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        spec,
      };
      await api(`/agents/${agent.id}`, { method: "PATCH", body: JSON.stringify(body) });
      toast.success(`Updated ${name.trim()}`);
      await onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={submitting || !presetsReady || !hasTeamModels || !canSaveLlm}
        >
          {submitting ? <Loader2 className="animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save changes
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="drawer-agent-name">Name</Label>
        <Input
          id="drawer-agent-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="drawer-agent-desc">Description</Label>
        <Input
          id="drawer-agent-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          placeholder="One-liner about what this agent does"
        />
      </div>

      <div className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
        System prompt is edited in the <span className="font-semibold">Prompt</span> tab — it
        survives this save unchanged. Knowledge bases and tools are edited under{" "}
        <span className="font-semibold">Capabilities</span>.
      </div>

      <fieldset className="space-y-3 rounded-lg border border-border/80 bg-card/40 p-3">
        <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">
          LLM spec
        </legend>
        <p className="text-[10px] leading-snug text-muted-foreground">
          Under <code className="text-[10px]">/llm</code>, each row is one{" "}
          <span className="font-medium text-foreground">model</span> — vendor, encrypted key, API
          model id, temperature, etc. Agents only choose{" "}
          <span className="font-medium text-foreground">which row</span>. Then pick a{" "}
          <span className="font-medium text-foreground">runtime</span>.
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="drawer-agent-model">Model</Label>
          <Select
            value={llmConfigId ?? modelPickUnset}
            onValueChange={(v) => setLlmConfigId(v === modelPickUnset ? null : v)}
            disabled={!hasTeamModels}
          >
            <SelectTrigger id="drawer-agent-model">
              <SelectValue placeholder="Select a model…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={modelPickUnset}>
                <span className="text-muted-foreground">Select a model…</span>
              </SelectItem>
              {llmConfigs === null ? (
                <SelectItem value="__loading__" disabled>
                  Loading…
                </SelectItem>
              ) : (
                llmConfigs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="font-mono">{c.slug}</span>
                    {c.display_name ? (
                      <span className="ml-2 text-xs text-muted-foreground">{c.display_name}</span>
                    ) : null}
                  </SelectItem>
                ))
              )}
              {llmConfigId && llmConfigs !== null && selectedConfig === null ? (
                <SelectItem value={llmConfigId}>
                  <span className="font-mono">{llmConfigId.slice(0, 8)}…</span>
                  <span className="ml-2 text-xs text-amber-600">missing from /llm</span>
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
          {llmConfigsError ? (
            <p className="text-xs text-destructive">{llmConfigsError}</p>
          ) : llmConfigs !== null && llmConfigs.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              The team has no LLM model rows yet (API{" "}
              <code className="text-[10px]">GET /v1/llm/configs</code> returned nothing). On{" "}
              <a
                href="/llm#llm-models-card"
                className="font-medium text-foreground underline-offset-2 hover:underline"
              >
                /llm
              </a>
              , click <span className="font-medium text-foreground">Add model</span> and save vendor,
              key, and API model id — that creates the rows agents choose here.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="drawer-agent-runtime">Runtime</Label>
          <Select value={runtime} onValueChange={(v) => setRuntime(v as AgentRuntimeName)}>
            <SelectTrigger id="drawer-agent-runtime">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AGENT_RUNTIMES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedConfig ? (
          <div className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <div className="font-medium text-foreground">
              API model id:{" "}
              <span className="font-mono text-foreground/90">{selectedConfig.model}</span>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
              <span>Slug: {selectedConfig.slug}</span>
              <span>Temperature: {selectedConfig.temperature.toFixed(2)}</span>
              <span>Max tokens: {selectedConfig.max_output_tokens ?? "—"}</span>
              {selectedConfig.provider_kind ? (
                <span>Kind: {selectedConfig.provider_kind}</span>
              ) : null}
            </div>
          </div>
        ) : null}

        {isOrphanPresetBinding ? (
          <p className="text-[11px] text-destructive">
            This bind points at a deleted /llm row. Choose another model above.
          </p>
        ) : null}
      </fieldset>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────
// Capabilities — inline edit
// ────────────────────────────────────────────────────────────────────

function CapabilitiesEditForm({
  agent,
  kbs,
  onCancel,
  onSaved,
}: {
  agent: AgentSummary;
  kbs: KbView[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const initialSpec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const [kbIds, setKbIds] = useState<Set<string>>(new Set(initialSpec.knowledge_base_ids));
  const [skillIds, setSkillIds] = useState<Set<string>>(
    new Set(initialSpec.skills.map((s) => s.id)),
  );
  const [shortTermBudget, setShortTermBudget] = useState(
    String(initialSpec.memory.short_term_token_budget),
  );
  const [summarizeEvery, setSummarizeEvery] = useState(
    initialSpec.memory.summarize_every_turns === null
      ? ""
      : String(initialSpec.memory.summarize_every_turns),
  );
  const [episodicRecall, setEpisodicRecall] = useState(initialSpec.memory.episodic_recall);
  const [guardrails, setGuardrails] = useState<Array<{ name: string; configText: string }>>(
    initialSpec.guardrails.map((g) => ({
      name: g.name,
      configText: Object.keys(g.config).length === 0 ? "" : JSON.stringify(g.config),
    })),
  );
  const [tools, setTools] = useState<EditableTool[]>(
    initialSpec.tools.map((t) => specToolToEditable(t)),
  );
  const [mcpServerIds, setMcpServerIds] = useState<Set<string>>(
    new Set(initialSpec.mcp_servers.map((m) => m.id)),
  );
  const [mcpServers, setMcpServers] = useState<ExternalMcpSummary[] | null>(null);
  const [mcpServersError, setMcpServersError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [installedSkills, setInstalledSkills] = useState<SkillSpec[] | null>(null);
  const [installedSkillsError, setInstalledSkillsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<ExternalMcpListResponse>("/mcp/external");
        if (!cancelled) setMcpServers(res.items);
      } catch (err) {
        if (!cancelled) {
          setMcpServers([]);
          setMcpServersError(err instanceof Error ? err.message : "Failed to load MCP servers");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const mcpServersById = useMemo<Map<string, ExternalMcpSummary>>(
    () => new Map((mcpServers ?? []).map((s) => [s.id, s])),
    [mcpServers],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<InstalledSkillsResponse>("/skills/installed");
        if (!cancelled) setInstalledSkills(res.items);
      } catch (err) {
        if (!cancelled) {
          setInstalledSkills([]);
          setInstalledSkillsError(
            err instanceof Error ? err.message : "Failed to load installed skills",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleSkill(id: string): void {
    setSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleKb(id: string): void {
    setKbIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function buildCapabilitiesSpec(): AgentSpec | null {
    const budgetNum = Number.parseInt(shortTermBudget, 10);
    const budget = Number.isFinite(budgetNum) && budgetNum > 0 ? budgetNum : 4000;
    const summarizeNum = summarizeEvery.trim() ? Number.parseInt(summarizeEvery, 10) : null;
    const summarize =
      summarizeNum !== null && Number.isFinite(summarizeNum) && summarizeNum > 0
        ? summarizeNum
        : null;
    const memory: AgentMemorySpec = {
      short_term_token_budget: budget,
      summarize_every_turns: summarize,
      episodic_recall: episodicRecall,
    };
    const parsedGuardrails: AgentGuardrailRef[] = [];
    for (const row of guardrails) {
      if (!row.name.trim()) continue;
      let config: Record<string, unknown> = {};
      const text = row.configText.trim();
      if (text) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            toast.error(`Guardrail "${row.name}": config must be a JSON object`);
            return null;
          }
          config = parsed as Record<string, unknown>;
        } catch (err) {
          toast.error(
            `Guardrail "${row.name}": ${err instanceof Error ? err.message : "invalid JSON"}`,
          );
          return null;
        }
      }
      parsedGuardrails.push({ name: row.name.trim(), config });
    }
    const parsedTools: AgentToolRef[] = [];
    for (const row of tools) {
      const trimmedName = row.name.trim();
      if (!trimmedName) continue;
      let extras: Record<string, unknown> = {};
      const text = row.extraConfigText.trim();
      if (text) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            toast.error(`Tool "${trimmedName}": config must be a JSON object`);
            return null;
          }
          extras = parsed as Record<string, unknown>;
        } catch (err) {
          toast.error(
            `Tool "${trimmedName}": ${err instanceof Error ? err.message : "invalid JSON"}`,
          );
          return null;
        }
      }
      let config: Record<string, unknown> = extras;
      if (row.kind === "mcp") {
        if (!row.serverId) {
          toast.error(`Tool "${trimmedName}": pick an MCP server`);
          return null;
        }
        config = { server_id: row.serverId, ...extras };
      }
      parsedTools.push({
        name: trimmedName,
        kind: row.kind,
        config,
        risk_tier: row.riskTier,
        policy: row.policy,
      });
    }
    const previousByName = new Map<string, string>(
      initialSpec.mcp_servers.map((m) => [m.id, m.name]),
    );
    const parsedMcpServers: AgentMcpServerRef[] = [];
    for (const id of mcpServerIds) {
      const previousName = previousByName.get(id);
      const registered = mcpServersById.get(id);
      const name = previousName ?? registered?.slug ?? id;
      parsedMcpServers.push({ id, name });
    }
    const previousSkillVersion = new Map<string, string | null>(
      initialSpec.skills.map((s) => [s.id, s.version]),
    );
    const parsedSkills: AgentSkillRef[] = Array.from(skillIds).map((id) => ({
      id,
      version: previousSkillVersion.get(id) ?? null,
    }));

    return {
      ...initialSpec,
      system_prompt: initialSpec.system_prompt,
      tools: parsedTools,
      mcp_servers: parsedMcpServers,
      knowledge_base_ids: Array.from(kbIds),
      memory,
      guardrails: parsedGuardrails,
      skills: parsedSkills,
    };
  }

  async function submit(): Promise<void> {
    const spec = buildCapabilitiesSpec();
    if (spec === null) return;
    setSubmitting(true);
    try {
      const body: UpdateAgentRequest = { spec };
      await api(`/agents/${agent.id}`, { method: "PATCH", body: JSON.stringify(body) });
      toast.success("Capabilities updated");
      await onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? <Loader2 className="animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save changes
        </Button>
      </div>

      <div className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
        Agent name, runtime, and model are edited in <span className="font-semibold">Setup</span>.
        System prompt lives in <span className="font-semibold">Prompt</span>.
      </div>

      <fieldset className="space-y-2 rounded-lg border border-border p-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Knowledge bases
        </legend>
        {kbs.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No knowledge bases yet. Create one in <code>/knowledge</code> to attach it here.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {kbs.map((kb) => {
              const checked = kbIds.has(kb.id);
              return (
                <label
                  key={kb.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleKb(kb.id)} />
                  <BookOpen className="h-3 w-3 text-muted-foreground" />
                  <span className="font-medium">{kb.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{kb.slug}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">
                    {kb.scope}
                  </Badge>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>

      <fieldset className="space-y-2 rounded-lg border border-border p-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Skills ({skillIds.size})
        </legend>
        <p className="text-[11px] text-muted-foreground">
          Each skill appends an instruction snippet to the system prompt and (where applicable)
          merges its tools into the allow-list at run time. Install skills under{" "}
          <code>/skills</code> first.
        </p>
        {(() => {
          if (installedSkills === null) {
            return <p className="text-xs text-muted-foreground">Loading…</p>;
          }
          const installedById = new Map(installedSkills.map((s) => [s.id, s]));
          const orphans = Array.from(skillIds).filter((id) => !installedById.has(id));
          if (installedSkills.length === 0 && orphans.length === 0) {
            return (
              <p className="text-xs text-muted-foreground">
                {installedSkillsError ?? (
                  <>
                    No skills installed. Browse the catalog at <code>/skills</code> and install at
                    least one to bind it here.
                  </>
                )}
              </p>
            );
          }
          return (
            <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
              {installedSkills.map((s) => {
                const checked = skillIds.has(s.id);
                return (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
                    title={s.description}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSkill(s.id)}
                      className="mt-0.5"
                    />
                    <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="flex-1 leading-tight">
                      <span className="block font-medium">{s.name}</span>
                      <span className="block text-[10px] text-muted-foreground">{s.tagline}</span>
                    </span>
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      {s.category}
                    </Badge>
                  </label>
                );
              })}
              {orphans.map((id) => (
                <label
                  key={id}
                  className="flex cursor-pointer items-start gap-2 rounded border border-amber-500/40 px-2 py-1 text-xs hover:bg-muted"
                  title="Bound but not installed for this team — uncheck to drop."
                >
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggleSkill(id)}
                    className="mt-0.5"
                  />
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                  <span className="flex-1 leading-tight">
                    <span className="block font-mono font-medium">{id}</span>
                    <span className="block text-[10px] text-amber-600">
                      Not installed — save will 422 unless dropped or installed.
                    </span>
                  </span>
                </label>
              ))}
            </div>
          );
        })()}
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-3 rounded-lg border border-border p-3 md:grid-cols-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Memory
        </legend>
        <div className="space-y-1.5">
          <Label htmlFor="cap-agent-stb">Short-term token budget</Label>
          <Input
            id="cap-agent-stb"
            type="number"
            min="1"
            value={shortTermBudget}
            onChange={(e) => setShortTermBudget(e.target.value)}
            placeholder="4000"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cap-agent-summarize">Summarize every N turns</Label>
          <Input
            id="cap-agent-summarize"
            type="number"
            min="1"
            value={summarizeEvery}
            onChange={(e) => setSummarizeEvery(e.target.value)}
            placeholder="(off)"
          />
        </div>
        <div className="flex items-end">
          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted">
            <input
              type="checkbox"
              checked={episodicRecall}
              onChange={(e) => setEpisodicRecall(e.target.checked)}
            />
            Episodic recall
          </label>
        </div>
      </fieldset>

      <fieldset className="space-y-2 rounded-lg border border-border p-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Guardrails ({guardrails.length})
        </legend>
        {guardrails.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No guardrails. Click <span className="font-semibold">Add guardrail</span> to wire one.
            Config is a free-form JSON object passed to the runtime.
          </p>
        ) : (
          <ul className="space-y-2">
            {guardrails.map((row, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: see comment
                key={i}
                className="grid grid-cols-1 items-start gap-2 md:grid-cols-[1fr_2fr_auto]"
              >
                <Input
                  value={row.name}
                  onChange={(e) => {
                    const next = [...guardrails];
                    next[i] = { ...row, name: e.target.value };
                    setGuardrails(next);
                  }}
                  placeholder="rule name (e.g. regex_block)"
                  className="font-mono text-xs"
                />
                <Input
                  value={row.configText}
                  onChange={(e) => {
                    const next = [...guardrails];
                    next[i] = { ...row, configText: e.target.value };
                    setGuardrails(next);
                  }}
                  placeholder='{"pattern": "…"}'
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 text-muted-foreground hover:text-destructive"
                  onClick={() => setGuardrails(guardrails.filter((_, j) => j !== i))}
                  aria-label="Remove guardrail"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => setGuardrails([...guardrails, { name: "", configText: "" }])}
        >
          <Plus className="h-3 w-3" />
          Add guardrail
        </Button>
      </fieldset>

      <fieldset className="space-y-2 rounded-lg border border-border p-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          MCP servers ({mcpServerIds.size})
        </legend>
        <p className="text-[11px] text-muted-foreground">
          Coarse-grained access — every tool the server exposes becomes callable. For per-tool
          gating (risk + approval), add a <code>kind=mcp</code> row in <em>Tools</em> below.
        </p>
        {mcpServers === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : mcpServers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {mcpServersError ?? (
              <>
                No MCP servers registered. Set one up under <code>/mcp</code>.
              </>
            )}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {mcpServers.map((s) => {
              const checked = mcpServerIds.has(s.id);
              return (
                <label
                  key={s.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted",
                    !s.enabled && "opacity-60",
                  )}
                  title={s.enabled ? s.url : `${s.url} (disabled — runtime will skip)`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setMcpServerIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(s.id)) next.delete(s.id);
                        else next.add(s.id);
                        return next;
                      });
                    }}
                  />
                  <span className="font-medium">{s.display_name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{s.slug}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">
                    {s.transport}
                  </Badge>
                  {!s.enabled ? <span className="text-[10px] text-amber-600">disabled</span> : null}
                </label>
              );
            })}
          </div>
        )}
      </fieldset>

      <fieldset className="space-y-2 rounded-lg border border-border p-3">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Tools ({tools.length})
        </legend>
        <p className="text-[11px] text-muted-foreground">
          Per-call wiring with optional <code>risk_tier</code> + <code>policy</code>. Builtins live
          in the runtime registry; pick <em>mcp</em> to expose a single named tool from a registered
          server.
        </p>
        {tools.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No tools yet. Click <span className="font-semibold">Add tool</span> to wire one.
          </p>
        ) : (
          <ul className="space-y-2">
            {tools.map((row, i) => (
              <ToolRowEditor
                // biome-ignore lint/suspicious/noArrayIndexKey: see comment
                key={i}
                row={row}
                mcpServers={mcpServers}
                onChange={(updater) => {
                  setTools((prev) => {
                    const next = [...prev];
                    next[i] = updater(next[i]);
                    return next;
                  });
                }}
                onRemove={() => setTools((prev) => prev.filter((_, j) => j !== i))}
              />
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setTools((prev) => [...prev, makeBlankTool("builtin")])}
          >
            <Plus className="h-3 w-3" />
            Add builtin tool
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setTools((prev) => [...prev, makeBlankTool("mcp")])}
            disabled={mcpServers !== null && mcpServers.length === 0}
            title={
              mcpServers !== null && mcpServers.length === 0
                ? "Register an MCP server under /mcp first"
                : undefined
            }
          >
            <Plus className="h-3 w-3" />
            Add MCP tool
          </Button>
        </div>
      </fieldset>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tool row editor — local state for a single AgentToolRef. Hoisted so
// the parent's render doesn't churn when only one row changes.
// ────────────────────────────────────────────────────────────────────

interface EditableTool {
  /** Per-row local id, never sent to the API — only here so React keys
   *  remain stable across array splice operations.
   *  TODO(knode): swap for crypto.randomUUID once we drop SSR-mode list rendering. */
  name: string;
  /** ``"http"`` is intentionally absent from the picker — see the gateway
   *  validator for why (kind reserved, no runtime dispatcher yet). */
  kind: "builtin" | "mcp";
  riskTier: ToolRiskTier;
  policy: ToolPolicy;
  /** ``server_id`` for ``mcp`` rows. Empty for builtins. */
  serverId: string;
  /** Free-form JSON object the operator can layer on top of the
   *  computed config. Stored as a string so users can save half-typed
   *  JSON without us reformatting (parse + validate happens on submit,
   *  same trade-off as guardrails). */
  extraConfigText: string;
}

function makeBlankTool(kind: EditableTool["kind"]): EditableTool {
  return {
    name: "",
    kind,
    riskTier: "safe",
    policy: "allow",
    serverId: "",
    extraConfigText: "",
  };
}

function specToolToEditable(t: AgentToolRef): EditableTool {
  // ``http`` shouldn't reach the editor — the gateway rejects it on
  // write — but if a legacy spec snuck one through, downgrade to mcp
  // (with no server picked) so the operator is forced to fix it before
  // re-saving.
  const kind: EditableTool["kind"] = t.kind === "http" ? "mcp" : t.kind;
  // Pull server_id out of config so the picker shows it; the rest of
  // the config (e.g. tool_name overrides) becomes the freeform extras.
  const { server_id, ...extras } = (t.config ?? {}) as Record<string, unknown>;
  const serverId = typeof server_id === "string" && server_id.length > 0 ? server_id : "";
  return {
    name: t.name,
    kind,
    riskTier: t.risk_tier ?? "safe",
    policy: t.policy ?? "allow",
    serverId,
    extraConfigText: Object.keys(extras).length === 0 ? "" : JSON.stringify(extras),
  };
}

function ToolRowEditor({
  row,
  mcpServers,
  onChange,
  onRemove,
}: {
  row: EditableTool;
  mcpServers: ExternalMcpSummary[] | null;
  onChange: (updater: (current: EditableTool) => EditableTool) => void;
  onRemove: () => void;
}) {
  return (
    <li className="space-y-2 rounded-md border border-border/60 bg-card/40 p-2">
      <div className="grid grid-cols-1 items-end gap-2 md:grid-cols-[1fr_2fr_auto]">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Kind</Label>
          <Select
            value={row.kind}
            onValueChange={(v) =>
              onChange((cur) => {
                const nextKind = v as EditableTool["kind"];
                // Switching to builtin clears server_id (won't be sent
                // anyway) so the next save doesn't carry stale state if
                // the operator then renames the tool.
                return nextKind === "builtin"
                  ? { ...cur, kind: nextKind, serverId: "" }
                  : { ...cur, kind: nextKind };
              })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="builtin">builtin</SelectItem>
              <SelectItem value="mcp">mcp</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Name</Label>
          {row.kind === "builtin" ? (
            <Select
              value={row.name || undefined}
              onValueChange={(v) => onChange((cur) => ({ ...cur, name: v }))}
            >
              <SelectTrigger className="h-8 font-mono text-xs">
                <SelectValue placeholder="pick a builtin" />
              </SelectTrigger>
              <SelectContent>
                {KNOWN_BUILTIN_TOOLS.map((name) => (
                  <SelectItem key={name} value={name}>
                    <span className="font-mono">{name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={row.name}
              onChange={(e) => onChange((cur) => ({ ...cur, name: e.target.value }))}
              placeholder="e.g. filesystem.read"
              className="h-8 font-mono text-xs"
            />
          )}
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 self-end text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label="Remove tool"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {row.kind === "mcp" ? (
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            MCP server
          </Label>
          <Select
            value={row.serverId || undefined}
            onValueChange={(v) => onChange((cur) => ({ ...cur, serverId: v }))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="pick a server" />
            </SelectTrigger>
            <SelectContent>
              {mcpServers === null ? (
                <SelectItem value="__loading__" disabled>
                  Loading…
                </SelectItem>
              ) : mcpServers.length === 0 ? (
                <SelectItem value="__empty__" disabled>
                  No servers — register one under /mcp
                </SelectItem>
              ) : (
                mcpServers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono">{s.slug}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{s.display_name}</span>
                    {!s.enabled ? (
                      <span className="ml-2 text-xs text-amber-600">disabled</span>
                    ) : null}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Risk tier
          </Label>
          <Select
            value={row.riskTier}
            onValueChange={(v) => onChange((cur) => ({ ...cur, riskTier: v as ToolRiskTier }))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TOOL_RISK_TIERS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Policy
          </Label>
          <Select
            value={row.policy}
            onValueChange={(v) => onChange((cur) => ({ ...cur, policy: v as ToolPolicy }))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TOOL_POLICIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Extra config (JSON object)
          </Label>
          <Input
            value={row.extraConfigText}
            onChange={(e) => onChange((cur) => ({ ...cur, extraConfigText: e.target.value }))}
            placeholder={row.kind === "mcp" ? '{"tool_name":"…"}' : "{}"}
            className="h-8 font-mono text-xs"
          />
        </div>
      </div>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// Prompt panel — own view ↔ inline edit (kept separate from Setup so the
// edit form there isn't dominated by a giant textarea, and so writing
// a long system prompt feels like writing in a real editor).
// ────────────────────────────────────────────────────────────────────

function PromptPanel({
  agent,
  canEdit,
  onSaved,
}: {
  agent: AgentSummary;
  canEdit: boolean;
  onSaved: () => Promise<void> | void;
}) {
  const initialSpec = agent.spec ?? DEFAULT_AGENT_SPEC;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialSpec.system_prompt);
  const [submitting, setSubmitting] = useState(false);

  // Reset both view + draft when the user picks a different agent in the
  // outer list. Without this, switching agents would surface the previous
  // agent's prompt as the editable draft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: agent.id is the trigger; setters are stable
  useEffect(() => {
    setEditing(false);
    setDraft(initialSpec.system_prompt);
  }, [agent.id]);

  async function save(): Promise<void> {
    setSubmitting(true);
    try {
      const body: UpdateAgentRequest = {
        // Only ship the spec — name/description are the Setup tab's job.
        // We re-send the full spec because the API treats it as a
        // replacement (creates a new immutable AgentVersion).
        spec: { ...initialSpec, system_prompt: draft },
      };
      await api(`/agents/${agent.id}`, { method: "PATCH", body: JSON.stringify(body) });
      toast.success("Prompt updated");
      await onSaved();
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save prompt");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          {editing
            ? "Editing system prompt — Save creates a new agent version."
            : `${initialSpec.system_prompt.length.toLocaleString()} chars`}
        </p>
        {canEdit && !editing ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => {
              setDraft(initialSpec.system_prompt);
              setEditing(true);
            }}
          >
            <Pencil className="h-3 w-3" />
            Edit prompt
          </Button>
        ) : null}
        {editing ? (
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                setEditing(false);
                setDraft(initialSpec.system_prompt);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => void save()}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </Button>
          </div>
        ) : null}
      </div>
      {editing ? (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="min-h-[400px] flex-1 resize-y font-mono text-xs leading-relaxed"
          placeholder="You are a helpful assistant…"
        />
      ) : initialSpec.system_prompt.trim() ? (
        <pre className="flex-1 overflow-auto whitespace-pre-wrap rounded-md border border-border/60 bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground">
          {initialSpec.system_prompt}
        </pre>
      ) : (
        <EmptyHint>
          No system prompt set.{" "}
          {canEdit ? "Click Edit prompt to add one." : "Ask an admin to set one."}
        </EmptyHint>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Versions panel
// ────────────────────────────────────────────────────────────────────

function VersionsPanel({ agent }: { agent: AgentSummary }) {
  const [versions, setVersions] = useState<AgentVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api<AgentVersion[]>(`/agents/${agent.id}/versions`);
      // Newest first; the API doesn't guarantee order so we sort
      // client-side to keep the "current" pin at the top.
      const sorted = [...rows].sort((a, b) => b.version - a.version);
      setVersions(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Drop to empty so the loading skeleton clears even on failure;
      // the error banner above tells the user what went wrong.
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [agent.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          Each Setup save creates a new immutable version. The runtime resolves the latest on the
          next request.
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="h-7"
          onClick={() => void refresh()}
          disabled={loading}
          aria-label="Refresh versions"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
        </Button>
      </div>
      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-[11px] text-destructive">
          {error}
        </div>
      ) : null}
      {loading && versions === null ? (
        <Skeleton className="h-12 w-full" />
      ) : versions && versions.length === 0 ? (
        <EmptyHint>No versions yet — save the spec once to create v1.</EmptyHint>
      ) : (
        <ul className="space-y-1">
          {versions?.map((v) => {
            const isCurrent = v.id === agent.current_version_id;
            return (
              <li
                key={v.id}
                className={cn(
                  "flex items-center gap-2 rounded-md border border-border/60 bg-card/60 px-2.5 py-1.5",
                  isCurrent && "border-emerald-500/40 bg-emerald-500/5",
                )}
              >
                <Badge
                  variant="outline"
                  className="h-5 rounded-sm border-border/70 px-1.5 font-mono text-[10px]"
                >
                  v{v.version}
                </Badge>
                <span className="flex-1 truncate font-mono text-[10px] text-muted-foreground">
                  {v.id.slice(0, 8)} · by {v.created_by_user_id.slice(0, 8)}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(v.created_at).toLocaleString()}
                </span>
                {isCurrent ? (
                  <span className="inline-flex h-4 items-center rounded-sm bg-emerald-500/15 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    current
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Channels panel
// ────────────────────────────────────────────────────────────────────

function ChannelsPanel({
  bindings,
  canManage,
  onChanged,
}: {
  bindings: ChannelBindingSummary[];
  canManage: boolean;
  onChanged: () => Promise<void> | void;
}) {
  // Track per-row in-flight PATCH so the user can't double-click and so
  // the row visually shows "settling" instead of looking unresponsive.
  const [pending, setPending] = useState<Set<string>>(new Set());

  async function toggle(b: ChannelBindingSummary, next: boolean): Promise<void> {
    setPending((prev) => new Set(prev).add(b.id));
    try {
      await api(`/channels/bindings/${b.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: next }),
      });
      toast.success(`${b.display_name?.trim() || b.slug} ${next ? "enabled" : "disabled"}`);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Toggle failed");
    } finally {
      setPending((prev) => {
        const copy = new Set(prev);
        copy.delete(b.id);
        return copy;
      });
    }
  }

  if (bindings.length === 0) {
    return (
      <EmptyHint>
        No inbound channels bound to this agent yet. Configure one under <code>/channels</code>{" "}
        (admin only).
      </EmptyHint>
    );
  }
  return (
    <ul className="space-y-1.5">
      {bindings.map((b) => {
        const isPending = pending.has(b.id);
        return (
          <li
            key={b.id}
            className={cn(
              "flex items-center gap-2 rounded-md border border-border/60 bg-card/60 px-2.5 py-2",
              !b.enabled && "opacity-60",
            )}
          >
            <Badge
              variant="outline"
              className="h-5 rounded-sm border-border/70 px-1.5 font-mono text-[10px]"
            >
              {b.kind}
            </Badge>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-medium text-foreground">
                {b.display_name?.trim() || b.slug}
              </div>
              <div className="truncate font-mono text-[10px] text-muted-foreground">
                {b.slug}
                {b.has_credentials ? (
                  <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">
                    · secret set
                  </span>
                ) : (
                  <span className="ml-1.5 text-amber-600">· no secret</span>
                )}
              </div>
            </div>
            {canManage ? (
              // We render a real <button> rather than a plain badge so
              // disabled state + keyboard activation work for free.
              <button
                type="button"
                disabled={isPending}
                onClick={() => void toggle(b, !b.enabled)}
                className={cn(
                  "inline-flex h-6 items-center gap-1 rounded-sm px-2 text-[10px] font-medium uppercase tracking-wide transition-colors",
                  b.enabled
                    ? "bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground hover:bg-muted/80",
                  isPending && "cursor-wait opacity-60",
                )}
                aria-label={b.enabled ? "Disable channel" : "Enable channel"}
              >
                {isPending ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      b.enabled ? "bg-emerald-500" : "bg-muted-foreground/50",
                    )}
                  />
                )}
                {b.enabled ? "Enabled" : "Disabled"}
              </button>
            ) : (
              <span
                className={cn(
                  "inline-flex h-5 items-center gap-1 rounded-sm px-1.5 text-[10px] font-medium uppercase tracking-wide",
                  b.enabled
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    b.enabled ? "bg-emerald-500" : "bg-muted-foreground/50",
                  )}
                />
                {b.enabled ? "Enabled" : "Disabled"}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ────────────────────────────────────────────────────────────────────
// Runs panel
// ────────────────────────────────────────────────────────────────────

interface RuntimeSession {
  id: string;
  agent_id: string;
  external_thread_key: string | null;
  title: string | null;
  runtime: string;
  status: string;
  last_response_id: string | null;
  runtime_session_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

interface RuntimeSessionList {
  object: "list";
  data: RuntimeSession[];
}

function RunsPanel({ agent }: { agent: AgentSummary }) {
  const [sessions, setSessions] = useState<RuntimeSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api<RuntimeSessionList>(
        `/activity/sessions?agent_id=${encodeURIComponent(agent.id)}&limit=20`,
      );
      setSessions(res.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Same deal as VersionsPanel: clear the skeleton even on failure
      // so the user can see the error banner instead of a stuck shimmer.
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agent.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          Last 20 sessions for this agent. Open one to resume the conversation in <code>/chat</code>
          .
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="h-7"
          onClick={() => void refresh()}
          disabled={loading}
          aria-label="Refresh sessions"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
        </Button>
      </div>
      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-[11px] text-destructive">
          {error}
        </div>
      ) : null}
      {loading && sessions === null ? (
        <Skeleton className="h-12 w-full" />
      ) : sessions && sessions.length === 0 ? (
        <EmptyHint>No sessions yet for this agent.</EmptyHint>
      ) : (
        <ul className="space-y-1">
          {sessions?.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-card/60 px-2.5 py-1.5"
            >
              <Badge
                variant="outline"
                className={cn(
                  "h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal",
                  s.status === "running" &&
                    "border-amber-500/40 text-amber-700 dark:text-amber-400",
                  s.status === "failed" && "border-destructive/40 text-destructive",
                )}
              >
                {s.status}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium text-foreground">
                  {s.title?.trim() || s.id.slice(0, 12)}
                </div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  {s.runtime} · {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      // Reopen in /chat. The chat page picks up the
                      // preselected agent and the session rail will show
                      // this session ready to resume — there's no direct
                      // "open this session id" deeplink yet.
                      setChatPreselectedAgentId(agent.id);
                      window.location.href = "/chat";
                    }}
                    aria-label="Open in chat"
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">Open agent in /chat</TooltipContent>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Misc helpers
// ────────────────────────────────────────────────────────────────────

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-6 text-center text-[11px] text-muted-foreground">
      {children}
    </div>
  );
}
